// API Security — AI Callers (v2 MUI).
//
// Three tabs: Caller classes / Prompt-injection log / SDKs & agents.
//
// Backend wiring preserved verbatim:
//   asec.aiCallersDistribution(siteId, { hours })
//   asec.aiCallersVendors(siteId, { hours })
//   asec.aiCallersPromptInjections(siteId, { hours, min_confidence: 50 })

import React from "react";
import {
  Stack, Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Select, MenuItem,
  LinearProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Bot, Activity, UserCheck, ShieldAlert, Terminal, PieChart, BarChart3,
  Sparkles, HelpCircle, CircleDashed, Rewind, Wrench, Zap, VenetianMask,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import { GhostButton } from "../../../../common/Button";
import AsecPage from "./AsecPage";
import { asecTabsSx, filterSelectSx } from "./asecUi";
import {
  AsecMetricStrip, AsecStatusPill, AsecMono, AsecSectionCard, AsecHeroStatValue,
} from "./components/asecPrimitives";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import {
  AsecEventsTableShell,
  AsecEventsHeadCell,
  AsecEventsBodyText,
  AsecEventsTableFooter,
  asecEventRowSx,
} from "./components/AsecEventsTable";

const HOUR_OPTIONS = [
  { value: 24, label: "Last 24 hours" },
  { value: 72, label: "Last 3 days" },
  { value: 168, label: "Last 7 days" },
];

const CLASS_META = {
  human_browser:  { Icon: UserCheck,     color: colors.primary[300],  bar: `linear-gradient(90deg,${colors.primary[300]},${colors.primary[500]})`, tag: { bg: colors.primary[100], fg: colors.primary[500], bd: colors.primary[200] } },
  unclassified:   { Icon: HelpCircle,    color: colors.neutral[400],  bar: colors.neutral[400], tag: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] } },
  unknown:        { Icon: CircleDashed,  color: colors.neutral[300],  bar: colors.neutral[300], tag: { bg: colors.neutral[100], fg: colors.neutral[600], bd: colors.neutral[200] } },
  bot_search:     { Icon: Bot,           color: "#22A85B",            bar: "#22A85B", tag: { bg: "#E8FBEF", fg: "#0f7a42", bd: "#C0EED4" } },
  sdk_generic:    { Icon: Terminal,      color: colors.yellow[200],   bar: colors.yellow[200], tag: { bg: "#FFF6D0", fg: "#7a5b10", bd: "#FFE89A" } },
  llm_agent:      { Icon: Sparkles,      color: "#8257C4",           bar: "#8257C4", tag: { bg: "#F3EEFE", fg: "#6B3FA0", bd: "#E2D6FA" } },
};

function classMeta(c) {
  const base = String(c || "").split("/")[0];
  return CLASS_META[base] || { Icon: Bot, color: colors.neutral[600], bar: colors.neutral[300], tag: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] } };
}

function ClassTag({ c, sm }) {
  const meta = classMeta(c);
  const Icon = meta.Icon;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.875,
      fontSize: sm ? 11 : 12.5, fontWeight: 600,
      px: sm ? 1 : 1.25, py: sm ? 0.375 : 0.5, borderRadius: "7px",
      backgroundColor: meta.tag.bg, color: meta.tag.fg, border: `1px solid ${meta.tag.bd}`,
      width: "fit-content",
    }}>
      <LayerdIcon icon={Icon} size="xs" color="inherit" style={{ color: meta.color }} />
      {c || "—"}
    </Box>
  );
}

const INJ_PATTERN_META = {
  "ignore-prior":       { Icon: Rewind,        conf: 70 },
  "tool-hijack":        { Icon: Wrench,        conf: 70 },
  "data-exfil-attempt": { Icon: Zap,           conf: 80 },
  "data-exfil":         { Icon: Zap,           conf: 80 },
  "jailbreak-persona":  { Icon: VenetianMask, conf: 70 },
  "jailbreak":          { Icon: VenetianMask,  conf: 70 },
};

function InjectionTag({ pattern, warn }) {
  const meta = INJ_PATTERN_META[pattern] || { Icon: ShieldAlert };
  const palette = warn
    ? { bg: "#FFF6D0", fg: "#7a5b10", bd: "#FFE89A" }
    : { bg: "#FEECEE", fg: "#A11228", bd: "#FBC9CF" };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      fontSize: 12, fontWeight: 600,
      px: 1, py: 0.25, borderRadius: "6px",
      backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
    }}>
      <LayerdIcon icon={meta.Icon} size="xs" />
      {pattern || "injection"}
    </Box>
  );
}

function timeAgo(ts) {
  if (!ts) return "—";
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60 * 1000) return `${Math.round(diff / 1000)} sec ago`;
  if (diff < 60 * 60 * 1000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.round(diff / 3600000)} hr ago`;
  return new Date(ts).toLocaleDateString();
}

function shortTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString();
}

function buildHeroMetrics(dist, hours, injections, vendors) {
  const total = dist.reduce((a, r) => a + Number(r[`rows_${hours}h`] || r.count || 0), 0);
  const llmRows = dist.filter((r) => String(r.caller_class || "").startsWith("llm_agent") || r.caller_class === "sdk_generic");
  const aiTraffic = llmRows.reduce((a, r) => a + Number(r[`rows_${hours}h`] || r.count || 0), 0);
  const classifiedRows = dist.filter((r) => {
    const c = String(r.caller_class || "");
    return c !== "unclassified" && c !== "unknown";
  });
  const classifiedN = classifiedRows.reduce((a, r) => a + Number(r[`rows_${hours}h`] || r.count || 0), 0);
  const unclassifiedN = dist.find((r) => r.caller_class === "unclassified") || { count: 0 };
  const unknownN = dist.find((r) => r.caller_class === "unknown") || { count: 0 };
  const classifiedPct = total > 0 ? ((classifiedN / total) * 100).toFixed(1) : "0.0";
  const aiPct = total > 0 ? ((aiTraffic / total) * 100).toFixed(2) : "0.00";
  const topVendor = vendors[0];

  return [
    {
      key: "ai-traffic",
      icon: Bot,
      highlight: true,
      label: "AI / agent traffic",
      value: (
        <AsecHeroStatValue
          primary={aiTraffic.toLocaleString()}
          pill={<AsecStatusPill kind="indigo">{aiPct}%</AsecStatusPill>}
        />
      ),
      sub: "sdk_generic + classified LLM agents",
    },
    {
      key: "requests",
      icon: Activity,
      label: `Requests · ${hours}h`,
      value: total.toLocaleString(),
      sub: total > 0 ? "live" : "no traffic",
    },
    {
      key: "classified",
      icon: UserCheck,
      label: "Classified",
      value: `${classifiedPct}%`,
      sub: total > 0
        ? `${((Number(unclassifiedN[`rows_${hours}h`] || unclassifiedN.count || 0) / total) * 100).toFixed(1)}% unclassified · ${((Number(unknownN[`rows_${hours}h`] || unknownN.count || 0) / total) * 100).toFixed(1)}% unknown`
        : "—",
    },
    {
      key: "injection",
      icon: ShieldAlert,
      label: "Injection ≥ 50",
      value: injections.length,
      valueSx: { color: "#A11228" },
      sub: `scanned LLM bodies · ${hours}h`,
    },
    {
      key: "top-sdk",
      icon: Terminal,
      label: "Top SDK",
      value: topVendor ? (topVendor.vendor || topVendor.sdk || topVendor.name || "—") : "—",
      sub: topVendor
        ? `${(topVendor.count || topVendor[`rows_${hours}h`] || 0).toLocaleString()} req · ${topVendor.source_header || topVendor.header || "X-Stainless-Lang"}`
        : "no SDK headers seen",
    },
  ];
}

function ClassesTab({ dist, hours }) {
  const total = dist.reduce((a, r) => a + Number(r[`rows_${hours}h`] || r.count || 0), 0);
  const pctDenominator = total > 0 ? total : 1;
  const sorted = [...dist].sort((a, b) =>
    Number(b[`rows_${hours}h`] || b.count || 0) - Number(a[`rows_${hours}h`] || a.count || 0));

  const segments = sorted.map((r) => {
    const n = Number(r[`rows_${hours}h`] || r.count || 0);
    const meta = classMeta(r.caller_class);
    return { name: r.caller_class, n, pct: (n / pctDenominator) * 100, color: meta.color, raw: meta };
  });
  let acc = 0;
  const circles = segments.map((s, i) => {
    const da = s.pct.toFixed(2);
    const dash = `${da} ${(100 - s.pct).toFixed(2)}`;
    const offset = (100 - acc).toFixed(2);
    acc += s.pct;
    return { ...s, dash, offset, key: `${s.name}-${i}` };
  });
  const topSegment = segments[0];

  return (
    <AsecSectionCard title="Caller class distribution" icon={BarChart3}
      right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>User-Agent + SDK header classification</Typography>}
      radius="12px" noPadding bodySx={{ p: 0 }}>
      <Box sx={{
        display: "grid", gridTemplateColumns: "180px 1fr", gap: 3,
        p: 2.25, alignItems: "center",
        borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: "#FAFBFC",
      }}>
        <Stack alignItems="center" spacing={1}>
          <Box sx={{ position: "relative", width: 148, height: 148 }}>
            <svg width="148" height="148" viewBox="0 0 42 42">
              <circle cx="21" cy="21" r="15.9" fill="none" stroke={colors.neutral[200]} strokeWidth="6" />
              {circles.map((c) => (
                <circle key={c.key} cx="21" cy="21" r="15.9" fill="none"
                  stroke={typeof c.color === "string" && c.color.includes("gradient") ? colors.primary[300] : c.color}
                  strokeWidth="6" strokeDasharray={c.dash} strokeDashoffset={c.offset}
                  transform="rotate(-90 21 21)" />
              ))}
            </svg>
            <Stack alignItems="center" justifyContent="center" sx={{ position: "absolute", inset: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: colors.neutral[1000] }}>
                {topSegment ? topSegment.pct.toFixed(1) : "0"}%
              </Typography>
              <Typography sx={{
                fontSize: 12,
                color: topSegment ? colors.neutral[700] : colors.neutral[600],
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mt: 0.375,
              }}>
                {topSegment ? (topSegment.name === "human_browser" ? "Human" : topSegment.name) : "—"}
              </Typography>
            </Stack>
          </Box>
          <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{total.toLocaleString()} total requests</Typography>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
          {sorted.slice(0, 6).map((r) => {
            const n = Number(r[`rows_${hours}h`] || r.count || 0);
            const pct = (n / pctDenominator) * 100;
            const meta = classMeta(r.caller_class);
            return (
              <Stack key={r.caller_class} direction="row" alignItems="center" spacing={1} sx={{ fontSize: 12 }}>
                <Box sx={{
                  width: 11, height: 11, borderRadius: "3px", flexShrink: 0,
                  background: typeof meta.color === "string" && meta.color.includes("gradient") ? colors.primary[300] : meta.color,
                }} />
                <Typography sx={{ fontSize: 12, color: colors.neutral[1000], flex: 1 }}>{r.caller_class}</Typography>
                <Typography sx={{ color: colors.neutral[700], fontVariantNumeric: "tabular-nums" }}>{pct.toFixed(1)}%</Typography>
              </Stack>
            );
          })}
        </Box>
      </Box>

      {sorted.length === 0 ? (
        <Box sx={{ p: 5, textAlign: "center", color: colors.neutral[700] }}>No classified traffic yet.</Box>
      ) : sorted.map((r) => {
        const n = Number(r[`rows_${hours}h`] || r.count || 0);
        const pct = (n / pctDenominator) * 100;
        const meta = classMeta(r.caller_class);
        return (
          <Box key={r.caller_class} sx={{
            display: "grid", gridTemplateColumns: "200px 1fr 150px", gap: 2,
            alignItems: "center", px: 2.25, py: 1.75,
            borderBottom: `1px solid ${colors.neutral[100]}`,
            "&:hover": { backgroundColor: colors.neutral[50] },
          }}>
            <ClassTag c={r.caller_class} />
            <Box sx={{ height: 12, borderRadius: "6px", backgroundColor: colors.neutral[200], overflow: "hidden" }}>
              <Box sx={{
                height: "100%", borderRadius: "6px",
                width: `${Math.max(0.4, pct)}%`, minWidth: pct < 0.4 ? "3px" : undefined,
                background: meta.bar,
              }} />
            </Box>
            <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 700 }}>{n.toLocaleString()}</Typography>{" "}
              <Typography component="span" sx={{ fontSize: 12, color: colors.neutral[700] }}>{pct.toFixed(1)}%</Typography>
            </Box>
          </Box>
        );
      })}
    </AsecSectionCard>
  );
}

function InjectionTab({ rows, onInvestigate }) {
  const [filter, setFilter] = React.useState("all");
  const [search, setSearch] = React.useState("");

  const patternCounts = rows.reduce((m, r) => {
    const p = r.pattern_kind || r.pattern || "";
    m[p] = (m[p] || 0) + 1;
    return m;
  }, {});

  const filtered = rows.filter((r) => {
    const p = r.pattern_kind || r.pattern || "";
    if (filter !== "all" && p !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = [r.caller_class, p, r.raw_path, r.src_ip].join(" ").toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const highConf = rows.filter((r) => Number(r.confidence || 0) >= 70).length;
  const medConf = rows.length - highConf;
  const chips = [
    { key: "all", label: "All", ct: rows.length },
    ...Object.entries(patternCounts).map(([p, ct]) => ({ key: p, label: p, ct })),
  ];

  return (
    <Stack spacing={1.75}>
      <AsecEventsFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search caller, pattern or path…"
        categoryFilter={filter}
        onCategoryChange={setFilter}
        chips={chips}
        action={<GhostButton size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}>Export</GhostButton>}
      />

      <AsecSectionCard title="Prompt-injection audit log" icon={ShieldAlert}
        right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>rows where the scanner returned confidence ≥ 50 · last 24h</Typography>}
        radius="12px" noPadding bodySx={{ p: 0 }}>
        <AsecEventsTableShell
          sx={{ border: 0, borderRadius: 0, p: 0 }}
          footer={(
            <AsecEventsTableFooter
              from={filtered.length ? 1 : 0}
              to={filtered.length}
              total={filtered.length}
              label="detections"
              summary={(
                <>
                  {rows.length} detection{rows.length === 1 ? "" : "s"} ≥ 50 ·{" "}
                  <strong style={{ color: "#A11228" }}>{highConf}</strong> high (≥70) · {medConf} medium
                </>
              )}
            />
          )}
        >
          <TableHead>
            <TableRow>
              {["Time", "Caller class", "Pattern", "Path", "Source IP", "Confidence", ""].map((h, i) => (
                <AsecEventsHeadCell key={h || "actions"} align={i === 5 || i === 6 ? "right" : "left"}>{h}</AsecEventsHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
                  {rows.length === 0
                    ? <>No prompt-injection detections in this window. The scanner only fires on bodies classified as <AsecMono>llm_agent/*</AsecMono> or <AsecMono>sdk_generic</AsecMono>.</>
                    : "No detections match this filter."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r, i) => {
              const conf = Number(r.confidence || 0);
              const warn = conf < 70;
              return (
                <TableRow key={r.request_id || i} sx={asecEventRowSx}>
                  <TableCell>
                    <Stack>
                      <AsecEventsBodyText sx={{ fontSize: 12 }}>{shortTime(r.ts)}</AsecEventsBodyText>
                      <Typography sx={{ color: colors.neutral[700], fontSize: 12 }}>{timeAgo(r.ts)}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell><ClassTag c={r.caller_class} sm /></TableCell>
                  <TableCell><InjectionTag pattern={r.pattern_kind || r.pattern} warn={warn} /></TableCell>
                  <TableCell>
                    <AsecEventsBodyText sx={{ fontSize: 12 }}>{r.raw_path || r.path || "—"}</AsecEventsBodyText>
                  </TableCell>
                  <TableCell>
                    <AsecEventsBodyText sx={{ fontSize: 12 }}>{r.src_ip || "—"}</AsecEventsBodyText>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                      <Box sx={{ width: 54 }}>
                        <LinearProgress variant="determinate" value={Math.min(100, conf)}
                          sx={{
                            height: 7, borderRadius: "4px", backgroundColor: colors.neutral[100],
                            "& .MuiLinearProgress-bar": { backgroundColor: warn ? "#C29021" : colors.red[100], borderRadius: "4px" },
                          }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, width: 26, textAlign: "right", color: warn ? "#7a5b10" : "#A11228" }}>{conf}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <GhostButton size="small" onClick={() => onInvestigate(r)}>Investigate</GhostButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </AsecEventsTableShell>
      </AsecSectionCard>
    </Stack>
  );
}

function AgentsTab({ vendors, hours }) {
  return (
    <AsecSectionCard title="Detected SDKs & LLM agents" icon={Terminal}
      right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>classified via X-LLM-Agent / X-Stainless-Lang headers</Typography>}
      radius="12px">
      {vendors.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", color: colors.neutral[700] }}>No SDK or LLM-agent traffic in this window.</Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1.75 }}>
          {vendors.map((v, i) => {
            const isAgent = (v.kind || v.type || "").toLowerCase() === "agent"
              || String(v.vendor || v.sdk || "").includes("agent");
            const headerSource = v.source_header || v.header
              || (String(v.vendor || "").includes("layerd") ? "X-Layerd-Agent"
                : String(v.vendor || "").toLowerCase().includes("langchain") ? "X-LLM-Agent: langchain"
                  : "X-Stainless-Lang");
            const count = Number(v.count || v[`rows_${hours}h`] || v.requests || 0);
            return (
              <Box key={v.vendor || v.sdk || i} sx={{
                border: `1px solid ${colors.neutral[200]}`, borderRadius: "11px", p: 1.75,
                display: "flex", flexDirection: "column", gap: 1.25,
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}
                    sx={{ fontSize: 12, fontWeight: 600 }}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: "8px",
                      backgroundColor: isAgent ? "#F3EEFE" : colors.neutral[100],
                      color: isAgent ? "#6B3FA0" : colors.neutral[700],
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <LayerdIcon icon={isAgent ? Sparkles : Terminal} size="sm" />
                    </Box>
                    {v.vendor || v.sdk || v.name || "—"}
                  </Stack>
                  <AsecStatusPill kind={isAgent ? "indigo" : "default"}>{isAgent ? "Agent" : "SDK"}</AsecStatusPill>
                </Stack>
                <Typography sx={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{count.toLocaleString()}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, color: colors.neutral[700] }}>
                  {headerSource.startsWith("X-") ? <AsecMono>{headerSource}</AsecMono> : headerSource}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
    </AsecSectionCard>
  );
}

function Body({ siteId, hours }) {
  const { showError } = useNotification();
  const [tab, setTab] = React.useState(0);
  const [dist, setDist] = React.useState([]);
  const [vendors, setVendors] = React.useState([]);
  const [injections, setInjections] = React.useState([]);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const [d, v, p] = await Promise.all([
        asec.aiCallersDistribution(siteId, { hours }),
        asec.aiCallersVendors(siteId, { hours }).catch(() => ({ data: { items: [] } })),
        asec.aiCallersPromptInjections(siteId, { hours, min_confidence: 50 }).catch(() => ({ data: { items: [] } })),
      ]);
      setDist((d.data && d.data.items) || []);
      setVendors((v.data && v.data.items) || []);
      setInjections((p.data && p.data.items) || []);
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
  }, [siteId, hours, showError]);
  React.useEffect(() => { reload(); }, [reload]);

  const total = dist.reduce((a, r) => a + Number(r[`rows_${hours}h`] || r.count || 0), 0);
  const tabKeys = ["classes", "injection", "agents"];

  const investigate = (row) => {
    if (row.request_id) {
      window.open(`/v2/api-security/risk-score?request_id=${encodeURIComponent(row.request_id)}`, "_blank");
    } else {
      showError("This event has no request_id — investigate via the Risk Score page.");
    }
  };

  return (
    <Stack spacing={2.5}>
      <AsecMetricStrip metrics={buildHeroMetrics(dist, hours, injections, vendors)} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={asecTabsSx}>
        <Tab label={`Caller classes`} icon={<LayerdIcon icon={PieChart} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Prompt-injection log (${injections.length})`} icon={<LayerdIcon icon={ShieldAlert} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`SDKs & agents (${vendors.length})`} icon={<LayerdIcon icon={Terminal} size="sm" color="inherit" />} iconPosition="start" />
      </Tabs>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: -1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[300] }} />
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
          Live · {total.toLocaleString()} req / window
        </Typography>
      </Stack>

      {tabKeys[tab] === "classes" && <ClassesTab dist={dist} hours={hours} />}
      {tabKeys[tab] === "injection" && <InjectionTab rows={injections} onInvestigate={investigate} />}
      {tabKeys[tab] === "agents" && <AgentsTab vendors={vendors} hours={hours} />}
    </Stack>
  );
}

export default function AiCallers() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [hours, setHours] = React.useState(24);
  return (
    <AsecPage
      featureKey="api.auth_primitives"
      helpTooltip="AI agent / LLM caller analytics. Every request is classified by User-Agent + 3 SDK headers (X-Layerd-Agent, X-LLM-Agent, X-Stainless-Lang). LLM-classified bodies are scanned for prompt-injection patterns."
      title="AI Callers"
      helmetTitle="API Security · AI Callers — Layerd"
      pageDescription={(
        <>
          Every request is classified by User-Agent + 3 SDK headers (<AsecMono>X-Layerd-Agent</AsecMono>, <AsecMono>X-LLM-Agent</AsecMono>, <AsecMono>X-Stainless-Lang</AsecMono>).
          LLM-classified bodies are scanned for prompt-injection patterns — <strong>ignore-prior</strong>, <strong>jailbreak-persona</strong>,{" "}
          <strong>tool-hijack</strong>, <strong>data-exfil-attempt</strong>.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Select
            size="small"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            sx={{ ...filterSelectSx, minWidth: 140 }}
          >
            {HOUR_OPTIONS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </Stack>
      )}
    >
      {(siteId) => <Body key={refreshKey} siteId={siteId} hours={hours} />}
    </AsecPage>
  );
}
